import { useMemo } from 'react'
import { useAllTeams } from './useAllTeams'

interface TeamNameLookupOptions {
  session?: {
    teamId?: any
    teamName?: string
    assignment?: {
      teamId?: any
      teamName?: string
    }
  }
  pendingTransfer?: {
    teamId?: number
    teamName?: string
  }
}

/**
 * Core logic for finding team name by ID.
 * This can be used both inside and outside of React components.
 */
export function createTeamNameLookup(
  allTeams: any[] = [],
  options: TeamNameLookupOptions = {}
) {
  return function getTeamNameById(
    teamId: number | null | undefined
  ): string | null {
    if (teamId == null) {
      return null
    }

    // Use all teams to find the team name, don't just limit to availableTeams
    const directMatch = allTeams.find(team => team.id === teamId)
    if (directMatch?.name && directMatch.name.trim().length > 0) {
      return directMatch.name
    }

    // Check session team name
    if (
      options.session?.teamId != null &&
      Number(options.session.teamId) === Number(teamId) &&
      typeof options.session.teamName === 'string' &&
      options.session.teamName.trim().length > 0
    ) {
      return options.session.teamName.trim()
    }

    // Check session assignment team name
    if (
      options.session?.assignment?.teamId != null &&
      Number(options.session.assignment.teamId) === Number(teamId) &&
      options.session.assignment.teamName &&
      options.session.assignment.teamName.trim().length > 0
    ) {
      return options.session.assignment.teamName.trim()
    }

    // Check pending transfer team name
    if (
      options.pendingTransfer?.teamId != null &&
      Number(options.pendingTransfer.teamId) === Number(teamId) &&
      options.pendingTransfer.teamName &&
      options.pendingTransfer.teamName.trim().length > 0
    ) {
      return options.pendingTransfer.teamName.trim()
    }

    return null
  }
}

export function useTeamNameById(options: TeamNameLookupOptions = {}) {
  const { teams: allTeams } = useAllTeams()

  const getTeamNameById = useMemo(
    () => createTeamNameLookup(allTeams, options),
    [allTeams, options]
  )

  return getTeamNameById
}
